import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, X, Save, Camera } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import { trUpper, VEHICLE_DELETE_NOTE } from "../lib/labels";
import { ocrVinFromImageFile } from "../lib/vinOcr";
import { useAuth } from "../context/AuthContext";
import { SmartSelect } from "../components/VehicleSpecFields";
import { VEHICLE_TYPES, brandsFor, modelsForType, enginesFor, bodiesFor, bodiesForModel, yearsForModel, fuelsFor, gearsFor, YEARS, FUEL_TYPES, TRANSMISSIONS, COLORS, stockImageFor } from "../lib/vehicleCatalog";
import { trimsFor } from "../lib/labels";

const inputCls = "w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

const RUHSAT_ENABLED = true; // kullanici onayiyla canliya alindi (2026-06-25)

export default function RegisterVehicle() {
  const { user } = useAuth();
  const [form, setForm] = useState({ vin: new URLSearchParams(window.location.search).get("vin") || "", vehicle_type: "Otomobil", brand: "", model: "", trim: "", year: new Date().getFullYear(), body_type: "", engine: "", fuel: "", transmission: "", km: "", plate: "", color: "", photo: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cardPhotoRef = useRef(null);
  const [cardPhotoUploading, setCardPhotoUploading] = useState(false);
  const ruhsatRef = useRef(null);
  const [ruhsatUploading, setRuhsatUploading] = useState(false);
  const [ruhsatPath, setRuhsatPath] = useState("");
  const [ocrVin, setOcrVin] = useState("");
  const [ocrState, setOcrState] = useState(null);
  const [vinExists, setVinExists] = useState(false);
  const navigate = useNavigate();

  // Madde 1/3: sasi girildikce otomatik sorgu; kayitli ise yeni kayit engellenir
  useEffect(() => {
    const vin = form.vin.trim().toUpperCase().replace(/\s/g, "");
    if (vin.length !== 17) { setVinExists(false); return; }
    const t = setTimeout(() => {
      api.get(`/vehicles/check-vin/${vin}`)
        .then((r) => setVinExists(!!r.data.exists))
        .catch(() => setVinExists(false));
    }, 500);
    return () => clearTimeout(t);
  }, [form.vin]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setU = (k) => (e) => setForm((f) => ({ ...f, [k]: trUpper(e.target.value) }));

  const autoPhoto = form.brand ? stockImageFor(form.brand, form.model) : "";
  const shownPhoto = form.photo || autoPhoto;

  const onTypeChange = (t) => setForm((f) => ({ ...f, vehicle_type: t, brand: "", model: "", trim: "", engine: "", body_type: "" }));

  const pickCardPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { setError("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { setError("Fotoğraf 5 MB'ı aşamaz"); return; }
    setCardPhotoUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setForm((prev) => ({ ...prev, photo: data.path }));
    } catch (err) { setError(formatApiError(err)); }
    setCardPhotoUploading(false);
  };

  const pickRuhsat = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { setError("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (f.size > 5 * 1024 * 1024) { setError("Fotoğraf 5 MB'ı aşamaz"); return; }
    setRuhsatUploading(true);
    setError("");
    setOcrVin("");
    setOcrState(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setRuhsatPath(data.path);
      setOcrState({ kind: "reading", text: "Ruhsat üzerindeki şasi numarası okunuyor..." });
      try {
        const found = await ocrVinFromImageFile(f);
        setOcrVin(found || "");
        const entered = form.vin.trim().toUpperCase().replace(/\s/g, "");
        if (!found) setOcrState({ kind: "unreadable", text: "Ruhsattan şasi numarası okunamadı; kaydınız yönetici incelemesine gider." });
        else if (!entered) { setForm((f2) => ({ ...f2, vin: found })); setOcrState({ kind: "ok", text: `Şasi numarası ruhsattan okundu ve forma eklendi: ${found}` }); }
        else if (entered === found) setOcrState({ kind: "ok", text: "Şasi numarası ruhsatla eşleşti — doğrulandı." });
        else setOcrState({ kind: "mismatch", text: `Ruhsattaki şasi (${found}) girdiğiniz numarayla eşleşmiyor; kayıt yönetici incelemesine gider.` });
      } catch { setOcrState({ kind: "unreadable", text: "Ruhsat okunamadı; kayıt yönetici incelemesine gider." }); }
    } catch (err) { setError(formatApiError(err)); }
    setRuhsatUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const vin = form.vin.trim().toUpperCase().replace(/\s/g, "");
    if (vin.length !== 17) {
      setError("Şasi (VIN) numarası 17 haneli olmalıdır.");
      return;
    }
    if (RUHSAT_ENABLED && !ruhsatPath && user?.role !== "admin") {
      setError("Ruhsat fotoğrafı yüklenmesi zorunludur (şasi doğrulaması için).");
      return;
    }
    if (vinExists) {
      setError("Bu şasi numarası ile daha önce araç kaydı oluşturulmuştur. Yeni kayıt oluşturamazsınız.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/my-vehicles", {
        vin, brand: form.brand, model: form.model, year: Number(form.year),
        plate: form.plate, km: Number(form.km) || 0, engine: form.engine,
        body_type: form.body_type, trim: form.trim, vehicle_type: form.vehicle_type,
        fuel: form.fuel, transmission: form.transmission, color: form.color,
        photo: form.photo || autoPhoto,
        ...(RUHSAT_ENABLED ? { ruhsat: ruhsatPath, ocr_vin: ocrVin || "" } : {}),
      });
      toast.success("Aracınız başarıyla kaydedildi");
      navigate(`/araclarim/${data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setVinExists(true);
        setError("Bu şasi numarası ile daha önce araç kaydı oluşturulmuştur. Yeni kayıt oluşturamazsınız.");
      } else {
        setError(formatApiError(err));
      }
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-16 carbon-texture">
      <div className="max-w-xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center">
            <Car className="w-5 h-5 text-cyan-300" />
          </span>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Aracımı Kaydet</h1>
        </div>
        <p className="mt-2 text-sm text-slate-400">
          Aracınızı dijital künyeye ekleyin; bakım, kaza, boya ve ekspertiz kayıtlarını tek yerden yönetin.
        </p>

        <form onSubmit={submit}
          className="mt-7 rounded-2xl backdrop-blur-xl bg-slate-900/80 border border-white/10 shadow-2xl p-5 sm:p-8 space-y-5 animate-fade-up">
          <div>
            <label className={labelCls}>Şasi No / VIN *</label>
            <input data-testid="reg-vehicle-vin" required value={form.vin} maxLength={17}
              onChange={(e) => setForm((f) => ({ ...f, vin: e.target.value.toUpperCase() }))}
              className={`${inputCls} font-mono-vin tracking-widest`} placeholder="17 haneli şasi numarası" />
            <p className="text-[11px] text-slate-500 mt-1">17 haneli şasi numarası (ruhsat veya kapı direği etiketinde yazar).</p>
            {vinExists && (
              <div data-testid="reg-vehicle-vin-exists" className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3.5">
                <p className="text-xs text-amber-200 font-semibold leading-relaxed">
                  Bu şasi numarası ile daha önce araç kaydı oluşturulmuştur. Yeni kayıt oluşturamazsınız.
                  Mevcut kaydın devralınması/devam ettirilmesi için yönetimle iletişime geçin.
                </p>
                <a data-testid="reg-vehicle-contact-link" href="/#iletisim"
                  className="inline-block mt-2 text-xs font-bold text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                  Yönetim ile İletişime Geç
                </a>
              </div>
            )}
          </div>

          <SmartSelect label="Araç Türü" value={form.vehicle_type} onChange={onTypeChange} options={VEHICLE_TYPES}
            testid="reg-vehicle-vehicle-type" inputCls={inputCls} labelCls={labelCls} required allowManual={false} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SmartSelect label="Marka" value={form.brand} options={brandsFor(form.vehicle_type)} required
              onChange={(v) => setForm((f) => ({ ...f, brand: v, model: "", trim: "", engine: "" }))}
              testid="reg-vehicle-brand" inputCls={inputCls} labelCls={labelCls} manualPlaceholder="Marka adını yazın" />
            <SmartSelect label="Model" value={form.model} options={modelsForType(form.brand, form.vehicle_type)} required
              onChange={(v) => setForm((f) => ({ ...f, model: v, engine: "" }))}
              testid="reg-vehicle-model" inputCls={inputCls} labelCls={labelCls} manualPlaceholder="Model adını yazın" />
            <SmartSelect label="Araç Paketi / Donanım" value={form.trim} options={trimsFor(form.brand)}
              onChange={(v) => setForm((f) => ({ ...f, trim: v }))}
              testid="reg-vehicle-trim" inputCls={inputCls} labelCls={labelCls} manualPlaceholder="Paket adını yazın (örn. TITANIUM PLUS)" />
            <SmartSelect label="Model Yılı" value={String(form.year || "")} options={yearsForModel(form.brand, form.model)} required allowManual={false}
              onChange={(v) => setForm((f) => ({ ...f, year: v }))}
              testid="reg-vehicle-year" inputCls={inputCls} labelCls={labelCls} />
            <SmartSelect label="Kasa Tipi" value={form.body_type} options={bodiesForModel(form.brand, form.model, form.vehicle_type)} allowManual={false}
              onChange={(v) => setForm((f) => ({ ...f, body_type: v }))}
              testid="reg-vehicle-body-type" inputCls={inputCls} labelCls={labelCls} />
            <SmartSelect label="Motor" value={form.engine} options={enginesFor(form.brand)}
              onChange={(v) => setForm((f) => ({ ...f, engine: v }))}
              testid="reg-vehicle-engine" inputCls={inputCls} labelCls={labelCls} manualPlaceholder="örn. 1.6 TDCI" />
            <SmartSelect label="Yakıt Tipi" value={form.fuel} options={fuelsFor(form.brand, form.model)} allowManual={false}
              onChange={(v) => setForm((f) => ({ ...f, fuel: v }))}
              testid="reg-vehicle-fuel" inputCls={inputCls} labelCls={labelCls} />
            <SmartSelect label="Vites" value={form.transmission} options={gearsFor(form.brand, form.model)} allowManual={false}
              onChange={(v) => setForm((f) => ({ ...f, transmission: v }))}
              testid="reg-vehicle-transmission" inputCls={inputCls} labelCls={labelCls} />
            <div>
              <label className={labelCls}>Kilometre</label>
              <input data-testid="reg-vehicle-km" type="number" min="0" value={form.km} onChange={set("km")}
                className={inputCls} placeholder="85000" />
            </div>
            <div>
              <label className={labelCls}>Plaka</label>
              <input data-testid="reg-vehicle-plate" value={form.plate} onChange={setU("plate")}
                className={inputCls} placeholder="34 ABC 123" />
            </div>
            <SmartSelect label="Renk" value={form.color} options={COLORS}
              onChange={(v) => setForm((f) => ({ ...f, color: v }))}
              testid="reg-vehicle-color" inputCls={inputCls} labelCls={labelCls} manualPlaceholder="Rengi yazın" />
          </div>

          <div>
            <label className={labelCls}>Profil Fotoğrafı</label>
            <input ref={cardPhotoRef} type="file" accept="image/jpeg,image/png" className="hidden"
              data-testid="reg-vehicle-photo-input" onChange={pickCardPhoto} />
            <div className="flex items-center gap-3">
              {shownPhoto ? (
                <div className="relative">
                  <img src={photoUrl(shownPhoto)} alt="" className="w-28 h-20 object-cover rounded-lg border border-white/10" />
                  {!form.photo && (
                    <span className="absolute bottom-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-950/80 text-cyan-300">Otomatik</span>
                  )}
                </div>
              ) : (
                <div className="w-28 h-20 rounded-lg border border-dashed border-white/15 bg-slate-950/60 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-slate-600" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <button type="button" data-testid="reg-vehicle-photo-btn" onClick={() => cardPhotoRef.current?.click()}
                  disabled={cardPhotoUploading}
                  className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                  {cardPhotoUploading ? "Yükleniyor..." : form.photo ? "Fotoğrafı Değiştir" : "Fotoğraf Ekle"}
                </button>
                {form.photo && (
                  <button type="button" data-testid="reg-vehicle-photo-clear"
                    onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                    className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-300 transition-colors">
                    <X className="w-3 h-3" /> Kaldır (otomatik görsele dön)
                  </button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Kendi fotoğrafınızı eklemezseniz araç bilgilerine uygun görsel otomatik gösterilir. JPG veya PNG, en fazla 5 MB. Diğer araç fotoğraflarını kayıt sonrası Fotoğraflar sekmesinden ekleyebilirsiniz.
            </p>
          </div>

          {RUHSAT_ENABLED && (
          <div data-testid="ruhsat-upload-card">
            <label className={labelCls}>Ruhsat (Şasi Doğrulama)</label>
            <input ref={ruhsatRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={pickRuhsat} />
            <div className="flex items-center gap-3">
              <button type="button" data-testid="reg-vehicle-ruhsat-btn" onClick={() => ruhsatRef.current?.click()}
                disabled={ruhsatUploading}
                className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 text-xs font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
                {ruhsatUploading ? "Yükleniyor..." : ruhsatPath ? "Ruhsatı Değiştir" : "Ruhsat Yükle (Kamera / Galeri)"}
              </button>
              {ruhsatPath && (
                <span data-testid="reg-vehicle-ruhsat-ok" className="text-xs font-semibold text-emerald-300">Ruhsat yüklendi</span>
              )}
              {ocrState && (
                <span data-testid="reg-vehicle-ruhsat-ocr" className={`text-xs font-semibold leading-snug ${ocrState.kind === "ok" ? "text-emerald-300" : ocrState.kind === "reading" ? "text-slate-400" : "text-amber-300"}`}>
                  {ocrState.text}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Ruhsattaki şasi numarası, girdiğiniz şasi numarasıyla karşılaştırılacak; eşleşirse araç "Doğrulandı" olur, eşleşmezse veya okunamazsa yönetici incelemesine gider.
            </p>
          </div>
          )}

          {error && <p data-testid="reg-vehicle-error" className="text-sm text-red-400">{error}</p>}

          <button data-testid="reg-vehicle-submit" type="submit" disabled={saving || cardPhotoUploading || ruhsatUploading || vinExists}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] active:scale-[0.99] transition-all disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Aracımı Kaydet"}
          </button>
          <p className="text-xs text-slate-500 text-center">
            Kayıt sonrası araç profilinizi ve geçmiş raporunuzu görüntüleyebilirsiniz.
          </p>
          <p data-testid="reg-vehicle-delete-note" className="text-xs text-amber-300/80 text-center">{VEHICLE_DELETE_NOTE}</p>
        </form>
      </div>
    </div>
  );
}
